import recentlyViewedStorage from './recently-viewed-storage.js'

const RV_LIMIT = 6

class CartProductTabs extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId || 'cart'
    this.cartHandles = this.parseCartHandles()
    this.root = this.querySelector('.cart-recommendations')
    this.tabsEl = this.querySelector('hh-tabs')
    this.tablistWrap = this.querySelector('.cart-recommendations__tablist-wrap')
    this.heading = this.querySelector('.cart-recommendations__heading')
    this.arrows = this.querySelector('.cart__arrows')
    this.rvPanel = this.querySelector('[data-recently-viewed-mount]')
    this.recPanel = this.querySelector('[data-rec-panel]')
    this.rvEnabled = this.dataset.recentlyViewedEnabled !== 'false'

    const rvHandles = this.getFilteredHandles()
    const recHasContent = this.recPanelHasContent()

    if (rvHandles.length === 0 && !recHasContent) {
      this.remove()
      return
    }

    if (rvHandles.length === 0) {
      this.showRecommendationsOnly()
      return
    }

    if (!recHasContent) {
      this.showRecentlyViewedOnly(rvHandles)
      return
    }

    this.enableTabs(rvHandles)
  }

  parseCartHandles() {
    try {
      const parsed = JSON.parse(this.dataset.cartProductHandles || '[]')
      return Array.isArray(parsed) ? parsed : []
    } catch (e) {
      return []
    }
  }

  getFilteredHandles() {
    if (!this.rvEnabled || !this.rvPanel) return []

    const cartSet = new Set(this.cartHandles)
    return recentlyViewedStorage
      .list()
      .filter((handle) => handle && !cartSet.has(handle))
      .slice(0, RV_LIMIT)
  }

  recPanelHasContent() {
    return Boolean(
      this.recPanel?.querySelector('.product-card-mini, .swiper-slide')
    )
  }

  getArrowPrev() {
    return this.querySelector('[data-cart-arrow-prev]')
  }

  getArrowNext() {
    return this.querySelector('[data-cart-arrow-next]')
  }

  getPrevSelector() {
    return `.cart-previous-${this.sectionId}`
  }

  getNextSelector() {
    return `.cart-next-${this.sectionId}`
  }

  renderRecentlyViewedCarousel(handles) {
    const id = `cart-rv-${this.sectionId}`
    const slideLabel = this.dataset.slideLabelMessage || ''
    const slides = handles
      .map(
        (handle) => `
      <div class='swiper-slide'>
        <dynamic-product-card handle='${handle}' template='product-card-mini'></dynamic-product-card>
      </div>
    `
      )
      .join('')

    const useCarousel = handles.length > 1

    if (!useCarousel) {
      this.rvPanel.innerHTML = `
        <dynamic-product-card handle='${handles[0]}' template='product-card-mini'></dynamic-product-card>
      `
      return null
    }

    this.rvPanel.innerHTML = `
      <p class='visually-hidden'>${this.dataset.carouselHelperText || ''}</p>
      <hh-swiper
        class='swiper-container'
        data-id='${id}'
        data-mobile-nav='true'
        data-custom-previous='${this.getPrevSelector()}'
        data-custom-next='${this.getNextSelector()}'
        ${slideLabel ? `data-slide-label-message='${slideLabel}'` : ''}
      >
        <div class='swiper swiper--${id}'>
          <div class='swiper-wrapper'>
            ${slides}
          </div>
        </div>
      </hh-swiper>
    `

    return this.rvPanel.querySelector('hh-swiper')
  }

  getActiveSwiperEl(panel = this.getActivePanel()) {
    return panel?.querySelector('hh-swiper') || null
  }

  getActivePanel() {
    if (this.root?.classList.contains('cart-recommendations--tabs-active')) {
      return this.rvPanel?.classList.contains('hidden')
        ? this.recPanel
        : this.rvPanel
    }

    if (!this.recPanel?.classList.contains('hidden')) return this.recPanel
    return this.rvPanel
  }

  getSlideCount(panel) {
    if (!panel) return 0
    const swiperSlides = panel.querySelectorAll('.swiper-slide')
    if (swiperSlides.length) return swiperSlides.length
    return panel.querySelector('.product-card-mini, dynamic-product-card')
      ? 1
      : 0
  }

  getAllSwiperEls() {
    return [...this.querySelectorAll('hh-swiper')]
  }

  unbindArrowsFromAllSwipers() {
    this.getAllSwiperEls().forEach((swiperEl) => {
      const swiper = swiperEl?.swiper
      if (!swiper?.navigation) return

      swiper.navigation.destroy()
      swiper.params.navigation.prevEl = null
      swiper.params.navigation.nextEl = null
    })
  }

  updateArrows() {
    const panel = this.getActivePanel()
    const slideCount = this.getSlideCount(panel)
    const showArrows = slideCount > 1

    this.arrows?.classList.toggle('hidden', !showArrows)

    if (!showArrows) {
      this.unbindArrowsFromAllSwipers()
      return
    }

    const swiperEl = this.getActiveSwiperEl(panel)
    if (swiperEl?.swiper) {
      this.rebindArrows(swiperEl)
      swiperEl.swiper.update()
    }
  }

  rebindArrows(swiperEl) {
    const swiper = swiperEl?.swiper
    const prevBtn = this.getArrowPrev()
    const nextBtn = this.getArrowNext()
    if (!swiper || !prevBtn || !nextBtn) return

    this.unbindArrowsFromAllSwipers()

    swiper.params.navigation.prevEl = prevBtn
    swiper.params.navigation.nextEl = nextBtn

    if (swiper.navigation) {
      swiper.navigation.init()
      swiper.navigation.update()
    }
  }

  waitForSwiper(swiperEl, callback) {
    if (!swiperEl) {
      callback()
      return
    }

    if (swiperEl.swiper) {
      callback()
      return
    }

    let timeoutId

    const onInit = (event) => {
      if (event.detail?.id === swiperEl.dataset.id) {
        document.removeEventListener('swiper:init', onInit)
        clearTimeout(timeoutId)
        callback()
      }
    }

    document.addEventListener('swiper:init', onInit)

    timeoutId = setTimeout(() => {
      document.removeEventListener('swiper:init', onInit)
      callback()
    }, 2000)
  }

  bindTabSwitch() {
    this.tabsEl?.querySelectorAll('.js-tab').forEach((tab) => {
      tab.addEventListener('click', () => {
        requestAnimationFrame(() => {
          const panel = this.getActivePanel()
          this.setRegionLabel(this.getPanelLabel(panel))
          const swiperEl = this.getActiveSwiperEl(panel)
          this.waitForSwiper(swiperEl, () => this.updateArrows())
        })
      })
    })
  }

  setRegionLabel(text) {
    if (this.root && text) this.root.setAttribute('aria-label', text)
  }

  setHeading(text) {
    if (this.heading) this.heading.textContent = text
    this.setRegionLabel(text)
  }

  getPanelLabel(panel) {
    return panel === this.rvPanel
      ? this.dataset.labelRecentlyViewed || ''
      : this.dataset.labelRecommended || ''
  }

  showRecommendationsOnly() {
    this.tablistWrap?.classList.add('hidden')
    this.rvPanel?.classList.add('hidden')
    this.recPanel?.classList.remove('hidden')
    this.setHeading(this.dataset.labelRecommended || '')
    this.root?.classList.remove('cart-recommendations--tabs-active')

    const swiperEl = this.getActiveSwiperEl(this.recPanel)
    this.waitForSwiper(swiperEl, () => this.updateArrows())
  }

  showRecentlyViewedOnly(handles) {
    this.tablistWrap?.classList.add('hidden')
    this.recPanel?.classList.add('hidden')
    this.rvPanel?.classList.remove('hidden')
    this.setHeading(this.dataset.labelRecentlyViewed || '')
    this.root?.classList.remove('cart-recommendations--tabs-active')

    const swiperEl = this.renderRecentlyViewedCarousel(handles)
    this.waitForSwiper(swiperEl, () => this.updateArrows())
  }

  enableTabs(handles) {
    this.root?.classList.add('cart-recommendations--tabs-active')
    this.heading?.classList.add('hidden')
    this.tablistWrap?.classList.remove('hidden')
    this.rvPanel?.classList.remove('hidden')
    this.recPanel?.classList.add('hidden')
    // Recently viewed is the active tab on load.
    this.setRegionLabel(this.dataset.labelRecentlyViewed || '')

    const swiperEl = this.renderRecentlyViewedCarousel(handles)
    this.bindTabSwitch()
    this.waitForSwiper(swiperEl, () => this.updateArrows())
  }
}

customElements.define('cart-product-tabs', CartProductTabs)
